import {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import {
  matchPath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { AccountSuspended } from '@/features/accounts'
import {
  getCurrentUser,
  getIsPayrollEnabled,
  isCanceled,
  selectIsUserMembershipPendingOrUnpaid,
  selectShouldHaveSubscription,
  selectHasAppAccess,
} from '@/selectors/user.selectors'
import AdminRoute from './admin-route'
import PrivateRoute from './private-route'
import PublicRoute from './public-route'
import ExternalAdminRoute from './external-admin-route'

import {
  ANNUAL_TAX_CENTER_ROUTE_CONFIG,
  PUBLIC_ROUTES,
  RouteConfig,
} from './config'
import { useRouteMapper } from './useRouteMapper'
import { getOnboardingRedirectTarget } from './onboardingRedirect'

import { useReselector } from '@/utils/sharedHooks'
import {
  FETCH_SUBSCRIPTIONS_KEY,
  fetchSubscriptions,
} from '@/reducers/subscription.slice'
import { useAnalyticsTrack } from '@/features/amplitude'
import { CanceledAccount } from '@/features/accounts/CanceledAccount'
import { useAppDispatch } from '@/utils/typeHelpers'
import { selectCatchUpBookkeepingIsUnpaid } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import { CatchUpBookkeepingUnpaid } from '@/features/accounts/CatchUpBookkeepingUnpaid'
import {
  FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS,
  fetchUserCatchUpBookkeepingStatus,
} from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { PAYMENT_SUCCESS_SEARCH_PARAM } from '@/actions/settings/billingActions'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { Loader } from '@/base'
import { Layout } from './Layout'
import { isHavenPath } from '@/features/adviser-chat/lib/havenRoutes'
import { useCheckoutStateContext } from '@/features/user/signup/payment/context'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { isCompliancePath } from '@/features/compliance/lib/constants'
import { getNoAppAccessRedirect } from './noAppAccessRedirect'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

enum RouteType {
  ADMIN_ROUTE = 'ADMIN_ROUTE',
  PRIVATE_ROUTE = 'PRIVATE_ROUTE',
  PUBLIC_ROUTE = 'PUBLIC_ROUTE',
  EXTERNAL_ADMIN_ROUTE = 'EXTERNAL_ADMIN_ROUTE',
}

const RouteTypeWrapper = {
  [RouteType.ADMIN_ROUTE]: AdminRoute,
  [RouteType.PRIVATE_ROUTE]: PrivateRoute,
  [RouteType.PUBLIC_ROUTE]: PublicRoute,
  // This allows for external (non-Heard) admins to access certain parts of Heard internal tooling (ex: TP)
  [RouteType.EXTERNAL_ADMIN_ROUTE]: ExternalAdminRoute,
}

const PUBLIC_ROUTE_PATHS = Object.keys(PUBLIC_ROUTES)

const LoggedInWrapper = ({
  children,
  useOldDesignSystem,
}: {
  children: ReactNode
  useOldDesignSystem: boolean
}) => {
  const { pathname } = useLocation()

  // Public routes don't use the LoggedInLayout
  const isPublicRoute = useMemo(
    () => PUBLIC_ROUTE_PATHS.some((path) => matchPath(path, pathname)),
    [pathname]
  )

  if (isPublicRoute) {
    return children
  }
  return <Layout useOldDesignSystem={useOldDesignSystem}>{children}</Layout>
}

const Router = () => {
  const {
    ADMIN_ROUTES,
    EXTERNAL_ADMIN_ROUTES,
    PRIVATE_ROUTES_ANNUAL_TAXES,
    PAYROLL_ROUTES,
    PRIVATE_ROUTES,
    PUBLIC_ROUTES,
  } = useRouteMapper()

  const userHasAppAccess = useReselector(selectHasAppAccess)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { pathname, search } = useLocation()

  const currentUser = useReselector(getCurrentUser)
  const isPayrollEnabled = useReselector(getIsPayrollEnabled)
  const isUserMembershipPendingOrUnpaid = useReselector(
    selectIsUserMembershipPendingOrUnpaid
  )
  // TEMP: Decide if old DS needs to be ported over for DS2
  const oldDSPaths = useMemo(
    () => [
      ...Object.keys(ADMIN_ROUTES),
      ...Object.keys(EXTERNAL_ADMIN_ROUTES),
      ...Object.keys(PRIVATE_ROUTES_ANNUAL_TAXES),
    ],
    [ADMIN_ROUTES, EXTERNAL_ADMIN_ROUTES, PRIVATE_ROUTES_ANNUAL_TAXES]
  )
  const shouldTempUseOldDesignSystemInNewDS = useMemo(
    () => oldDSPaths.some((p) => matchPath(p, pathname)),
    [oldDSPaths, pathname]
  )

  const catchUpBookkeepingIsUnpaid = useReselector(
    selectCatchUpBookkeepingIsUnpaid
  )
  const isCanceledAccount = useReselector(isCanceled)

  const shouldCheckForSubscriptions = useReselector(
    selectShouldHaveSubscription
  )
  const [searchParams] = useSearchParams()
  const { checkoutSuccess, setCheckoutSuccess } = useCheckoutStateContext()
  const track = useAnalyticsTrack()

  const showCatchUpBKPaymentScreen = useMemo(() => {
    return (
      !pathname.includes('/signup') &&
      pathname !== '/accounts' &&
      !checkoutSuccess &&
      catchUpBookkeepingIsUnpaid
    )
  }, [pathname, checkoutSuccess, catchUpBookkeepingIsUnpaid])

  const isLoading = useReselector(selectIsFetchingForKeys, [
    FETCH_SUBSCRIPTIONS_KEY,
    FETCH_USER_CATCH_UP_BOOKKEEPING_STATUS,
  ])

  const disable2024AnnualTax = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.disable2024AnnualTax,
    false
  )

  useEffect(() => {
    if (searchParams.get(PAYMENT_SUCCESS_SEARCH_PARAM) === 'true') {
      setCheckoutSuccess(true)
    }
  }, [searchParams, setCheckoutSuccess])

  // We want to fetch subscriptions on every page change to ensure account suspended state is up to date
  useEffect(() => {
    if (shouldCheckForSubscriptions) {
      dispatch(fetchSubscriptions())
    }
  }, [shouldCheckForSubscriptions, dispatch])

  useEffect(() => {
    if (!currentUser?.admin && currentUser?.id) {
      fetchUserCatchUpBookkeepingStatus()(dispatch)
    }
  }, [dispatch, currentUser?.admin, currentUser?.id])

  useEffect(() => {
    if (checkoutSuccess && !currentUser?.admin && currentUser?.id) {
      fetchUserCatchUpBookkeepingStatus()(dispatch)
    }
  }, [checkoutSuccess, dispatch, currentUser?.admin, currentUser?.id])

  // Global Page View tracking for customers only (non-admin)
  useEffect(() => {
    track('App Page View')
  }, [pathname, track])

  useEffect(() => {
    if (!isLoading && currentUser && userHasAppAccess === false) {
      // The compliance funnel (intake → report → plan) runs pre-membership, so it must
      // neither be swept into the main signup/onboarding flow nor forgotten between
      // sessions — on a fresh login the user lands on "/", not on a funnel path.
      const redirectTo = getNoAppAccessRedirect({
        pathname,
        search,
        lastOnboardingScreen: currentUser.lastOnboardingScreen,
      })
      if (redirectTo && redirectTo !== pathname) {
        navigate(redirectTo)
      }
    }
    if (
      !isLoading &&
      currentUser &&
      userHasAppAccess === true &&
      currentUser.admin === false &&
      currentUser.onboardingItemsCompletedAt === null
    ) {
      // We require the user to complete onboarding before they can access the app
      // (excluding for the accounts page, which is needed for billing, /user-cancellation,
      // and /plan-change)
      // Messages and Resources access is controlled by the feature flag
      if (
        !pathname.includes('/accounts') &&
        !pathname.includes('/onboarding-v2') &&
        !pathname.includes('/user-cancellation') &&
        !pathname.includes('/plan-change') &&
        !isCompliancePath(pathname) &&
        !isHavenPath(pathname)
      ) {
        navigate(getOnboardingRedirectTarget(pathname))
      }
    }
  }, [currentUser, userHasAppAccess, navigate, pathname, search, isLoading])

  const routeMapper = useCallback(
    (type: RouteType) => {
      const RouteWrapper = RouteTypeWrapper[type]
      return ([path, { customAuth, component: RouteComponent, ...rest }]: [
        string,
        RouteConfig[string],
      ]) => (
        <Route
          element={
            <RouteWrapper
              component={RouteComponent}
              {...(customAuth &&
                currentUser && {
                  customAuth: () => customAuth(currentUser),
                })}
            />
          }
          key={`router-route-${path}`}
          path={path}
          {...rest}
        />
      )
    },
    [currentUser]
  )

  if (isCanceledAccount) {
    return (
      <LoggedInWrapper useOldDesignSystem={shouldTempUseOldDesignSystemInNewDS}>
        <Routes>
          <>
            <Route element={<CanceledAccount />} path={'/accounts/canceled'} />
            <Route
              element={<Navigate to="/accounts/canceled" replace />}
              path="*"
            />
          </>
        </Routes>
      </LoggedInWrapper>
    )
  }

  return (
    <LoggedInWrapper useOldDesignSystem={shouldTempUseOldDesignSystemInNewDS}>
      <Loader loading={isLoading} />
      <Routes>
        {isUserMembershipPendingOrUnpaid ? (
          <>
            <Route
              element={<AccountSuspended />}
              path={'/accounts/suspended'}
            />
            <Route
              element={<Navigate to="/accounts/suspended" replace />}
              path="*"
            />
          </>
        ) : showCatchUpBKPaymentScreen ? (
          <>
            <Route
              element={<CatchUpBookkeepingUnpaid />}
              path={'/accounts/suspended/catch-up-bookkeeping'}
            />
            <Route
              element={
                <Navigate
                  to="/accounts/suspended/catch-up-bookkeeping"
                  replace
                />
              }
              path="*"
            />
          </>
        ) : (
          <>
            {Object.entries(ADMIN_ROUTES).map(
              routeMapper(RouteType.ADMIN_ROUTE)
            )}
            {isPayrollEnabled &&
              Object.entries(PAYROLL_ROUTES).map(
                routeMapper(RouteType.PRIVATE_ROUTE)
              )}
            {Object.entries(PRIVATE_ROUTES).map(
              routeMapper(RouteType.PRIVATE_ROUTE)
            )}
            {Object.entries(PUBLIC_ROUTES).map(
              routeMapper(RouteType.PUBLIC_ROUTE)
            )}
            {Object.entries(EXTERNAL_ADMIN_ROUTES).map(
              routeMapper(RouteType.EXTERNAL_ADMIN_ROUTE)
            )}
            {disable2024AnnualTax
              ? Object.entries({
                  'taxes/annual/*': ANNUAL_TAX_CENTER_ROUTE_CONFIG,
                }).map(routeMapper(RouteType.PRIVATE_ROUTE))
              : Object.entries(PRIVATE_ROUTES_ANNUAL_TAXES).map(
                  routeMapper(RouteType.PRIVATE_ROUTE)
                )}
          </>
        )}
      </Routes>
    </LoggedInWrapper>
  )
}

// Lives in BaseLayout and is used to display banner components specific to routes
export const BannerRouter = () => {
  const allRoutes = useRouteMapper().ALL_ROUTES

  return (
    <Routes>
      {Object.entries(allRoutes).map(
        ([path, { banner }]): JSX.Element | null => {
          if (!banner) return null

          if (Array.isArray(banner)) {
            return (
              <Route
                path={path}
                key={path}
                element={
                  <>
                    {banner.map((Ban, index) => {
                      const Component = Ban as ComponentType
                      return <Component key={index} />
                    })}
                  </>
                }
              />
            )
          }

          const Banner = banner as unknown as ComponentType
          return <Route path={path} key={path} element={<Banner />} />
        }
      )}
      <Route path="*" element={null} />
    </Routes>
  )
}

export default Router
